import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers/internalApi";
import type {
  ChatSortType,
  ChatType,
} from "@circle-react/types/CommunityBot/ChatSort";
import type { Chat } from "@circle-react/types/CommunityInbox/Chat";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import {
  CHAT_SORT_VALUES,
  CHAT_TYPES,
} from "@/react/components/SettingsApp/CommunityInbox/Sidebar/constants";
import { useCommunityInboxStore } from "@/react/hooks/chatsV2/store";

interface ParamsProp {
  agentId?: number;
  chatType?: ChatType;
  filter?: string;
  isPinned?: boolean;
  per_page?: number;
  sort?: ChatSortType;
}

interface ApiParamsProp extends ParamsProp {
  agent_id?: number;
  chat_type: ChatType;
  page: number;
}

interface ChatListPageResponse {
  has_next_page?: boolean;
  page?: number;
  records?: Chat[];
}

export const useChatList = (params: ParamsProp = {}) => {
  const {
    filter = "",
    per_page = 20,
    sort = CHAT_SORT_VALUES.LATEST,
    chatType = CHAT_TYPES.ALL,
    agentId = 0,
    isPinned = false,
  } = params;
  const { setChats, appendChats } = useCommunityInboxStore();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [chatType, filter, isPinned, agentId, sort]);

  const fetchChats = (page: number) => {
    const params: ApiParamsProp = {
      page,
      filter,
      per_page,
      sort,
      chat_type: chatType,
    };
    if (agentId !== 0) {
      params["agent_id"] = agentId;
    }
    return reactQueryGet(
      internalApi.communityInbox.index({
        params,
      }),
    );
  };

  const { data = {}, isPending } = useQuery<ChatListPageResponse>({
    queryKey: [
      "community-inbox",
      filter,
      per_page,
      sort,
      chatType,
      agentId,
      isPinned,
      page,
    ],
    queryFn: () => fetchChats(page),
  });

  useEffect(() => {
    if (isPinned || isPending) {
      return;
    }

    const { page: responsePage = 1, records = [] } = data;
    const inboxChatType = filter === "mine" ? "member" : "admin";

    if (responsePage === 1) {
      setChats(records, inboxChatType);
    } else {
      appendChats(records, inboxChatType);
    }
  }, [
    appendChats,
    agentId,
    chatType,
    data,
    filter,
    isPending,
    isPinned,
    page,
    setChats,
    sort,
  ]);

  const chats = data?.records || [];
  const hasNextPage = data?.has_next_page || false;

  const fetchNextPage = () => {
    if (hasNextPage && data.page != null) {
      setPage(data.page + 1);
    }
  };

  const isInitialPageLoading = isPending && page === 1;

  return {
    chats,
    isChatsLoading: isInitialPageLoading,
    hasNextPage,
    fetchNextPage,
  };
};
